// Centralized configuration for the Relaxing Routine application
// This file contains all configuration constants used throughout the project

export const config = {
  // Authentication & Security
  auth: {
    jwtSecret:"your_super_secure_jwt_secret_here",
      // process.env.JWT_SECRET || (() => { if (process.env.NODE_ENV === "production") throw new Error("JWT_SECRET environment variable is required in production"); return "dev_only_jwt_secret_change_in_production"; })(),
    jwtExpiresIn: "1d",
    refreshTokenExpiresIn: "7d",
    sessionDuration: 24 * 60 * 60, // 1 day in seconds
    refreshSessionDuration: 7 * 24 * 60 * 60, // 7 days in seconds
  },

  // Database
  database: {
    mongoUri:
      process.env.MONGODB_URI ||
      process.env.MONGODB_URI || "",
    // Mongoose connection options
    mongooseOptions: {
      bufferCommands: false,
      maxPoolSize: 10,
      serverSelectionTimeoutMS: 5000,
      socketTimeoutMS: 45000,
      family: 4,
    },
    // MongoDB native driver options (for socket server)
    nativeOptions: {
      maxPoolSize: 10,
      serverSelectionTimeoutMS: 5000,
      socketTimeoutMS: 45000,
      family: 4,
    },
    // Legacy connection options (keeping for backwards compatibility)
    connectionOptions: {
      bufferCommands: false,
      maxPoolSize: 10,
      serverSelectionTimeoutMS: 5000,
      socketTimeoutMS: 45000,
      family: 4,
    },
  },

  // Cookies
  cookies: {
    authToken: "auth_token",
    user: "user",
    session: "session",
    token: "token", // Legacy cookie name
    adminToken: "admin_token",
    adminUser: "admin_user",
    // HttpOnly options for secure auth tokens
    authOptions: {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict" as const,
      path: "/",
      maxAge: 24 * 60 * 60, // 1 day
    },
    // Client-readable options for user data
    userOptions: {
      httpOnly: false, // Allow client-side access for user data
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax" as const,
      path: "/",
      maxAge: 24 * 60 * 60, // 1 day
    },
    // Legacy options (keeping for backwards compatibility)
    options: {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict" as const,
      path: "/",
      maxAge: 24 * 60 * 60, // 1 day
    },
    strictOptions: {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict" as const,
      path: "/",
      maxAge: 7 * 24 * 60 * 60, // 7 days
    },
  },

  // API Configuration
  api: {
    baseUrl: process.env.NEXT_PUBLIC_API_URL || "",
    timeout: 30000, // 30 seconds
    retryAttempts: 3,
  },

  // Payment Gateway (Razorpay)
  payment: {
    razorpay: {
      keyId: process.env.RAZORPAY_KEY_ID || "",
      keySecret: process.env.RAZORPAY_KEY_SECRET || "",
      currency: "INR",
      companyName: "Relaxing Routine",
      description: "Class booking payment",
    },
  },

  // File Upload
  upload: {
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedImageTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
    allowedVideoTypes: ["video/mp4", "video/webm", "video/ogg"],
  },

  // Pagination
  pagination: {
    defaultLimit: 10,
    maxLimit: 100,
    defaultPage: 1,
  },

  // Community
  community: {
    maxTagsPerPost: 5,
    maxTagLength: 20,
    maxTitleLength: 100,
    maxContentLength: 10000,
    categories: [
      // Yoga & Movement
      "Yoga Practice",
      "Advanced Yoga",
      "Prenatal Yoga",

      // Breathing & Meditation
      "Pranayama & Breathing",
      "Meditation",
      "Mindfulness",

      // Fitness & Dance
      "Zumba & Dance Fitness",
      "Self Defense",
      "General Fitness",

      // Wellness & Health
      "Nutrition & Diet",
      "Ayurveda",
      "Mental Health",
      "Women's Wellness",

      // Lifestyle & Practice
      "Home Practice",
      "Daily Routine",
      "Equipment & Gear",
      "Class Experiences",
      "Instructor Tips",
      "Student Journey",

      // General
      "General Discussion",
      "Success Stories",
      "Questions & Help",
    ],
  },

  // Admin
  admin: {
    emails: [
      "admin@relaxingroutine.com",
      "aarav@relaxingroutine.com",
    ] as readonly string[],
    roles: ["admin", "ADMIN"] as readonly string[],
    permissions: [
      "manage_users",
      "manage_classes",
      "manage_instructors",
      "manage_students",
      "manage_blogs",
      "view_analytics",
      "system_settings",
    ] as readonly string[],
  },

  // Test Data — only available in development/test environments
  test: process.env.NODE_ENV === "production" ? undefined : {
    tokens: {
      instructor: "test_instructor_token",
      student: "test_student_token",
      admin: "test_admin_token",
    },
    users: {
      instructor: {
        userId: "test_instructor_123",
        email: "test.instructor@example.com",
        role: "INSTRUCTOR",
      },
      student: {
        userId: "test_student_123",
        email: "test.student@example.com",
        role: "STUDENT",
      },
      admin: {
        adminId: "test_admin_123",
        email: "test.admin@example.com",
        role: "ADMIN",
      },
    },
  },

  // Routes
  routes: {
    auth: {
      login: "/api/auth/login",
      logout: "/api/auth/logout",
      register: "/api/auth/register",
      verifyToken: "/api/auth/verify-token",
    },
    admin: {
      login: "/api/admin/login",
      register: "/api/admin/register",
      dashboard: "/admin/dashboard",
      community: "/admin/community",
    },
    instructor: {
      login: "/api/instructors/login",
      logout: "/api/instructors/logout",
      dashboard: "/instructors/dashboard",
    },
    student: {
      dashboard: "/profile",
    },
    community: {
      base: "/api/community",
      create: "/community/create",
      view: "/community",
    },
  },

  // Environment
  env: {
    isDevelopment: process.env.NODE_ENV === "development",
    isProduction: process.env.NODE_ENV === "production",
    isTest: process.env.NODE_ENV === "test",
  },

  // Feature Flags
  features: {
    enableCommunity: true,
    enableChat: true,
    enableLiveStreams: true,
    enablePayments: true,
    enableNotifications: true,
  },

  // Cache
  cache: {
    ttl: 60 * 60, // 1 hour in seconds
    maxSize: 100, // Maximum number of cached items
  },
} as const;

// Helper functions for common config operations
export const getJwtSecret = () => config.auth.jwtSecret;
export const getMongoUri = () => config.database.mongoUri;
export const getCookieName = (type: keyof typeof config.cookies) => {
  return type === "options" || type === "strictOptions"
    ? undefined
    : config.cookies[type];
};
export const isAdmin = (email: string, role?: string) => {
  return (
    config.admin.emails.includes(email) ||
    email.includes("admin") ||
    (role && config.admin.roles.some((r) => role.includes(r)))
  );
};
export const getRedirectUrl = (role: string) => {
  switch (role.toUpperCase()) {
    case "INSTRUCTOR":
      return config.routes.instructor.dashboard;
    case "STUDENT":
      return config.routes.student.dashboard;
    case "ADMIN":
      return config.routes.admin.dashboard;
    default:
      return "/";
  }
};

export default config;
